"use client";

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { Users, Copy, ArrowRight, ArrowDown, Zap } from "lucide-react";
import GradientAvatar from "@/app/components/GradientAvatar";
import { useT } from "@/hooks/useT";

/**
 * HeroV3Showcase — the right-hand "cockpit" of the copy-trading hero.
 *
 * The headline sells one thing: copy the best Polymarket traders in the
 * same block. So the cockpit shows the COPY story, not a generic markets
 * terminal. Three cards, all driven by the real trader leaderboard data
 * the page already fetches (`/api/social/traders/most-copied` → gems
 * fallback). No dependency on live copy_trade volume (sparse in beta),
 * so the cards are always populated:
 *
 *   1. SameBlockCopyCard — visualizes the differentiator: a real leader
 *      trades → you mirror it in the same block, to your own wallet.
 *      Rotates through the top copyable traders so it feels alive.
 *   2. MostCopiedCard    — the most-copied trader with a one-tap Copy CTA.
 *      Shows the real copiers count only when > 0 (honest in beta).
 *   3. TopLeadersCard    — mini-leaderboard of the top traders to copy.
 *
 * Market/whale liveliness still lives in the top ticker + bottom strip,
 * so dropping the market cards here doesn't make the hero feel static.
 */

interface TraderRow {
  wallet?: string;
  user_id?: string;
  address?: string;
  username?: string;
  display_name?: string;
  image?: string | null;
  profile_image?: string | null;
  pnl_30d?: number;
  pnl?: number;
  roi?: number;
  volume?: number;
  total_trades?: number;
  copiers_count?: number;
}

interface ShowcaseProps {
  traders: TraderRow[];
  /** PnL sparkline series keyed by trader address (lowercase). */
  traderSparklines?: Record<string, number[]>;
  // Accepted for backwards-compat with the caller but no longer rendered
  // here — the cockpit is copy-only now (see file header).
  markets?: unknown[];
  tape?: unknown[];
  whales?: unknown[];
  moves?: unknown[];
}

const ROTATE_MS = 9000;

export default function HeroV3Showcase({
  traders,
  traderSparklines,
}: ShowcaseProps) {
  // Ranked views of the same trader set, derived once.
  const byCopiers = useMemo(
    () =>
      [...traders].sort(
        (a, b) =>
          (b.copiers_count ?? 0) - (a.copiers_count ?? 0) ||
          pnlOf(b) - pnlOf(a),
      ),
    [traders],
  );
  const byPerf = useMemo(
    () =>
      [...traders].sort(
        (a, b) => roiOf(b) - roiOf(a) || pnlOf(b) - pnlOf(a),
      ),
    [traders],
  );

  const [leaderIdx, setLeaderIdx] = useState(0);
  const [mcIdx, setMcIdx] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const a = setInterval(
      () => setLeaderIdx((i) => (byPerf.length ? (i + 1) % byPerf.length : 0)),
      ROTATE_MS,
    );
    const b = setInterval(
      () => setMcIdx((i) => (byCopiers.length ? (i + 1) % byCopiers.length : 0)),
      ROTATE_MS + 2000,
    );
    return () => {
      clearInterval(a);
      clearInterval(b);
    };
  }, [byPerf.length, byCopiers.length]);

  const sameBlockTrader = byPerf[leaderIdx % Math.max(1, byPerf.length)];
  const mostCopied = byCopiers[mcIdx % Math.max(1, byCopiers.length)];
  const topLeaders = byPerf.slice(0, 3);

  const slots: Array<{ key: string; node: React.ReactNode }> = [
    { key: "sameblock", node: <SameBlockCopyCard trader={sameBlockTrader} /> },
    {
      key: "mostcopied",
      node: (
        <MostCopiedCard
          trader={mostCopied}
          sparkline={readSparkline(mostCopied, traderSparklines)}
        />
      ),
    },
    { key: "leaders", node: <TopLeadersCard traders={topLeaders} /> },
  ];

  return (
    <div className="relative w-full">
      {/* Mobile: simple stack */}
      <div className="grid grid-cols-1 gap-3 lg:hidden">
        {slots.map((s) => (
          <FlatCard key={s.key}>{s.node}</FlatCard>
        ))}
      </div>

      {/* Desktop: alternating right/left floating stack with even rhythm. */}
      <div
        className="relative hidden lg:block"
        style={{ minHeight: "clamp(420px, 48vh, 560px)" }}
      >
        {(() => {
          const bottomAnchor = 64;
          const stride = slots.length > 1 ? bottomAnchor / (slots.length - 1) : 0;
          const zClasses = ["z-[30]", "z-[38]", "z-[46]"];

          return slots.map((slot, i) => {
            const isRight = i % 2 === 0;
            const rotate = isRight ? -1.2 : 1.2;
            const top = `${i * stride}%`;
            return (
              <FloatingCard
                key={slot.key}
                className={`absolute w-[64%] ${
                  isRight ? "right-0" : "left-0"
                } ${zClasses[i] ?? "z-[30]"}`}
                style={{ top }}
                rotate={rotate}
                delay={0.08 + i * 0.12}
              >
                {slot.node}
              </FloatingCard>
            );
          });
        })()}

        {/* Ambient glows */}
        <div className="pointer-events-none absolute -top-10 right-10 w-[420px] h-[420px] bg-white/5 dark:bg-white/[0.04] rounded-full blur-[120px]" />
        <div className="pointer-events-none absolute bottom-0 left-10 w-[380px] h-[380px] bg-slate-400/10 dark:bg-slate-500/[0.06] rounded-full blur-[120px]" />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Card shells
   ───────────────────────────────────────────────────────────── */

function FloatingCard({
  className,
  style,
  rotate = 0,
  delay = 0,
  children,
}: {
  className?: string;
  style?: React.CSSProperties;
  rotate?: number;
  delay?: number;
  children: React.ReactNode;
}) {
  return (
    <motion.div
      style={style}
      initial={{ opacity: 0, y: 24, rotate }}
      animate={{ opacity: 1, y: 0, rotate }}
      transition={{ duration: 0.7, delay, ease: [0.2, 0.65, 0.3, 1] }}
      whileHover={{
        y: -10,
        scale: 1.035,
        rotate: rotate * 0.2,
        transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] },
      }}
      className={`${className ?? ""} hover:z-[60]`}
    >
      <div className="rounded-[5px] border border-gray-200 dark:border-white/10 bg-white/95 dark:bg-[#0f0f12]/92 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.18)] dark:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] hover:shadow-[0_32px_80px_-18px_rgba(0,0,0,0.28)] dark:hover:shadow-[0_52px_120px_-20px_rgba(0,0,0,0.75)] transition-shadow duration-300">
        {children}
      </div>
    </motion.div>
  );
}

function FlatCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[5px] border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0f0f12] shadow-sm">
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Card bodies
   ───────────────────────────────────────────────────────────── */

/**
 * SameBlockCopyCard — visualizes the core promise: a real leader trades,
 * and FrenFlow mirrors it to your own non-custodial wallet in the same
 * block. The leader identity (avatar / name / ROI) is real; the "same
 * block" framing describes the product, not a specific fabricated fill.
 */
function SameBlockCopyCard({ trader }: { trader?: TraderRow }) {
  const t = useT();
  if (!trader) return <CardSkeleton label={t("home.card.sameBlock")} />;

  const wallet = trader.address || trader.wallet || trader.user_id || "";
  const name = traderName(trader, t("home.card.traderFallback"));
  const avatar = trader.image || trader.profile_image;
  const roi = trader.roi != null ? Number(trader.roi) : null;
  const pnl = pnlOf(trader);
  const href = traderHref(trader);

  return (
    <Link href={href} prefetch={false} className="block p-5">
      <div className="flex items-center gap-2 mb-4">
        <Zap className="w-3.5 h-3.5 text-emerald-500" />
        <span className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {t("home.card.sameBlock")}
        </span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={wallet || name}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.3 }}
        >
          {/* Leader row */}
          <div className="flex items-center gap-3">
            {avatar ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={avatar}
                alt={name}
                className="w-10 h-10 rounded-[5px] object-cover flex-shrink-0"
                loading="lazy"
              />
            ) : (
              <GradientAvatar seed={wallet || name} size={40} className="!rounded-[5px] flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <div className="font-inter-tight text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400">
                {t("home.card.sameBlock.leader")}
              </div>
              <div className="font-inter-tight text-sm font-semibold text-gray-900 dark:text-white truncate">
                {name}
              </div>
            </div>
            {roi != null ? (
              <span className="font-inter-tight text-[12px] font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                {roi >= 0 ? "+" : ""}
                {(roi * 100).toFixed(0)}% {t("home.card.roi")}
              </span>
            ) : pnl !== 0 ? (
              <span
                className={`font-inter-tight text-[12px] font-semibold tabular-nums ${
                  pnl > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"
                }`}
              >
                {formatPnl(pnl)}
              </span>
            ) : null}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Connector: same-block latency pill */}
      <div className="flex items-center gap-2 my-3 pl-[18px]">
        <ArrowDown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-[5px] bg-emerald-500/10">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </span>
          <span className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
            {t("home.card.sameBlock.latency")}
          </span>
        </span>
      </div>

      {/* You row */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center flex-shrink-0">
          <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-inter-tight text-sm font-semibold text-gray-900 dark:text-white">
            {t("home.card.sameBlock.you")}
          </div>
          <div className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {t("home.card.sameBlock.sub")}
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * MostCopiedCard — the most-copied trader with a one-tap Copy CTA. The
 * "{n} copiers" chip renders only when there are real copy relationships
 * (> 0); in beta the gems fallback has no copiers, so we just lead with
 * ROI / PnL instead of showing a fake count.
 */
function MostCopiedCard({
  trader,
  sparkline,
}: {
  trader?: TraderRow;
  sparkline?: number[];
}) {
  const t = useT();
  if (!trader) return <CardSkeleton label={t("home.card.mostCopied")} />;

  const wallet = trader.address || trader.wallet || trader.user_id || "";
  const name = traderName(trader, t("home.card.traderFallback"));
  const avatar = trader.image || trader.profile_image;
  const pnl = pnlOf(trader);
  const roi = trader.roi != null ? Number(trader.roi) : null;
  const copiers = trader.copiers_count ?? 0;
  const href = traderHref(trader);
  const up = pnl >= 0;

  const [flashKey, setFlashKey] = useState(0);
  useEffect(() => {
    setFlashKey((k) => k + 1);
  }, [wallet || name]);

  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-3.5 h-3.5 text-purple-500" />
        <span className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {t("home.card.mostCopied")}
        </span>
        {copiers > 0 && (
          <span className="ml-auto inline-flex items-center gap-1 font-inter-tight text-[10px] text-gray-400 dark:text-gray-500">
            <Copy className="w-3 h-3" />
            {t("home.card.copiers", { count: copiers })}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-4">
        {avatar ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatar}
            alt={name}
            className="w-11 h-11 rounded-[5px] object-cover flex-shrink-0"
            loading="lazy"
          />
        ) : (
          <GradientAvatar seed={wallet || name} size={44} className="!rounded-[5px] flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={wallet || name}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3 }}
            >
              <div className="font-inter-tight text-sm font-semibold text-gray-900 dark:text-white truncate">
                {name}
              </div>
              <div className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400 truncate">
                {t("home.card.verifiedTrader")}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <div className="font-inter-tight text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1">
            {t("home.card.pnl30d")}
          </div>
          <FlashValue
            flashKey={flashKey}
            up={up}
            className={`font-inter-tight text-[26px] font-semibold tabular-nums leading-none ${
              pnl > 0
                ? "text-emerald-600 dark:text-emerald-400"
                : pnl < 0
                ? "text-red-500"
                : "text-gray-900 dark:text-white"
            }`}
          >
            {formatPnl(pnl)}
          </FlashValue>
          {roi != null && (
            <div
              className={`font-inter-tight text-[11px] font-semibold tabular-nums mt-1 ${
                roi >= 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-red-500 dark:text-red-400"
              }`}
            >
              {roi >= 0 ? "+" : ""}
              {(roi * 100).toFixed(1)}% {t("home.card.roi")}
            </div>
          )}
        </div>
        <TraderSparkline data={sparkline} up={up} />
      </div>

      {/* One-tap Copy CTA → trader profile (where the copy modal lives). */}
      <Link
        href={href}
        prefetch={false}
        className="flex items-center justify-center gap-2 w-full px-3 py-2.5 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight text-[13px] font-semibold hover:opacity-90 transition-opacity"
      >
        <Copy className="w-3.5 h-3.5" />
        {t("home.card.copyCta")}
      </Link>
    </div>
  );
}

/**
 * TopLeadersCard — compact leaderboard of the top traders to copy, ranked
 * by ROI. Mirrors the /traders experience in miniature.
 */
function TopLeadersCard({ traders }: { traders: TraderRow[] }) {
  const t = useT();
  if (traders.length === 0) return <CardSkeleton label={t("home.card.topToCopy")} />;

  return (
    <div className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-3.5 h-3.5 text-purple-500" />
        <span className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          {t("home.card.topToCopy")}
        </span>
      </div>

      <div className="space-y-1">
        {traders.map((trader, i) => {
          const wallet = trader.address || trader.wallet || trader.user_id || "";
          const name = traderName(trader, t("home.card.traderFallback"));
          const avatar = trader.image || trader.profile_image;
          const roi = trader.roi != null ? Number(trader.roi) : null;
          const pnl = pnlOf(trader);
          const copiers = trader.copiers_count ?? 0;
          const href = traderHref(trader);
          return (
            <Link
              key={wallet || name || i}
              href={href}
              prefetch={false}
              className="group flex items-center gap-2.5 -mx-2 px-2 py-1.5 rounded-[5px] hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
            >
              <span className="font-inter-tight text-[11px] font-semibold text-gray-400 dark:text-gray-500 w-3 tabular-nums">
                {i + 1}
              </span>
              {avatar ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={avatar}
                  alt={name}
                  className="w-7 h-7 rounded-[5px] object-cover flex-shrink-0"
                  loading="lazy"
                />
              ) : (
                <GradientAvatar seed={wallet || name} size={28} className="!rounded-[5px] flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <div className="font-inter-tight text-[13px] font-semibold text-gray-900 dark:text-white truncate">
                  {name}
                </div>
                {copiers > 0 && (
                  <div className="font-inter-tight text-[10px] text-gray-400 dark:text-gray-500 truncate">
                    {t("home.card.copiers", { count: copiers })}
                  </div>
                )}
              </div>
              <span className="font-inter-tight text-[13px] font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                {roi != null
                  ? `${roi >= 0 ? "+" : ""}${(roi * 100).toFixed(0)}%`
                  : formatPnl(pnl)}
              </span>
            </Link>
          );
        })}
      </div>

      <Link
        href="/traders"
        prefetch={false}
        className="mt-3 pt-3 border-t border-gray-100 dark:border-white/5 flex items-center justify-center gap-1.5 font-inter-tight text-[12px] font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
      >
        {t("home.card.seeAll")}
        <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Shared helpers
   ───────────────────────────────────────────────────────────── */

function CardSkeleton({ label }: { label: string }) {
  return (
    <div className="p-5">
      <div className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
        {label}
      </div>
      <div className="space-y-2">
        <div className="h-3 rounded-[5px] bg-gray-200 dark:bg-white/5 w-3/4" />
        <div className="h-3 rounded-[5px] bg-gray-200 dark:bg-white/5 w-1/2" />
      </div>
    </div>
  );
}

/** One-shot highlight when `flashKey` changes (green up / red down). */
function FlashValue({
  flashKey,
  up,
  className,
  children,
}: {
  flashKey: number;
  up: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <motion.span
      key={flashKey}
      initial={{ backgroundColor: up ? "rgba(0, 200, 83, 0.28)" : "rgba(255, 23, 68, 0.28)" }}
      animate={{ backgroundColor: "rgba(0,0,0,0)" }}
      transition={{ duration: 1.1, ease: "easeOut" }}
      className={`inline-block px-1 -mx-1 rounded-[3px] ${className || ""}`}
    >
      {children}
    </motion.span>
  );
}

/** PnL sparkline — same series shown on /traders. */
function TraderSparkline({ data, up }: { data?: number[]; up: boolean }) {
  const normalized = useMemo(() => {
    const series = data && data.length >= 3 ? data : null;
    if (series) return normalize(series.slice(-40));
    return up
      ? [22, 20, 21, 19, 18, 17, 15, 13, 14, 11, 9, 10, 8, 6, 5, 4]
      : [4, 6, 5, 8, 9, 10, 11, 13, 12, 14, 16, 15, 18, 19, 17, 20];
  }, [data, up]);

  const color = up ? "#00C853" : "#FF1744";
  const path = normalized
    .map((y, i) => `${i === 0 ? "M" : "L"} ${(i / (normalized.length - 1)) * 100} ${y}`)
    .join(" ");
  const area = `${path} L 100 24 L 0 24 Z`;

  return (
    <svg
      className="w-[130px] h-12 flex-shrink-0"
      viewBox="0 0 100 24"
      preserveAspectRatio="none"
      aria-hidden
    >
      <defs>
        <linearGradient id="hv3-trader-spark" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#hv3-trader-spark)" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function readSparkline(
  trader: TraderRow | undefined,
  map?: Record<string, number[]>,
): number[] | undefined {
  if (!trader || !map) return undefined;
  const raw = trader.address || trader.wallet || "";
  if (!raw) return undefined;
  return map[raw] || map[raw.toLowerCase()] || undefined;
}

function pnlOf(t: TraderRow): number {
  return Number(t.pnl_30d ?? t.pnl ?? 0);
}

function roiOf(t: TraderRow): number {
  return t.roi != null ? Number(t.roi) : 0;
}

function traderName(t: TraderRow, fallback: string): string {
  const wallet = t.address || t.wallet || t.user_id || "";
  return (
    t.username ||
    t.display_name ||
    (wallet.startsWith("0x") ? `${wallet.slice(0, 6)}…${wallet.slice(-4)}` : fallback)
  );
}

function traderHref(t: TraderRow): string {
  const wallet = t.address || t.wallet || t.user_id || "";
  return t.username ? `/traders/@${t.username}` : wallet ? `/traders/${wallet}` : "/traders";
}

function normalize(arr: number[]): number[] {
  if (arr.length === 0) return [12];
  let min = Infinity;
  let max = -Infinity;
  for (const v of arr) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const range = max - min || 1;
  return arr.map((v) => 22 - ((v - min) / range) * 20);
}

function formatPnl(n: number): string {
  if (!Number.isFinite(n) || n === 0) return "$0";
  const sign = n > 0 ? "+" : "−";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${Math.round(abs).toLocaleString()}`;
}
